import {useCallback, useEffect, useRef, useState} from "react";
import type {ReactNode} from "react";
import styled from "styled-components";
import {CountdownCircleTimer} from "react-countdown-circle-timer";
import {Functions} from "../../../utils/function/Functions";
import {MathUtils} from "../../../utils/math/MathUtils";
import {IconButtonV5} from "../interaction/IconButtonV5";
import {ButtonV5} from "../button/ButtonV5";
import XMarkIcon from "../icons/xmark.svg?react";
import {baseDistance, ELLIPSIS, FLEXCENTER, FlexCenterStyle, fontSize, fontWeight, radius, VerticalFlexStyle, zIndex} from "../styles/styles";
import {colorPalette} from "../styles/colorPalette";
import ExclamationAltIcon from "../icons/exclamation-alt.svg?react";
import CheckIcon from "../icons/check.svg?react";
import InformationAltIcon from "../icons/information-alt.svg?react";
import PinIcon from "../icons/location-pin.svg?react";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {appHolder} from "../../../utils/AppUtils";

/**
 * error: red border, X on the right side,
 * message: blue border, no X
 * warning: yellow border, no X
 * success: green border, undo button and X on the right side,
 * announcement: full width after nav bar, yellow background, X on the right side
 */
export enum NotificationType {
	Error = 1,
	Message = 2,
	Warning = 3,
	Success = 4,
	Announcement = 5,
}

export interface INotificationParamsV5 {
	title: string | ReactNode;
	description?: string;
	readonly link?: string;
	type: NotificationType;
	readonly className?: string;
	readonly cancelable?: boolean;
	buttonLabel?: string; // Action button appear only if buttonLabel is passed
	readonly style?: React.CSSProperties;
	// If you want it to be there until the user explicitly closes it, pass "Infinity" here
	// Any non-valid and/or non-positive number is accepted, and work the same though
	lifeTime?: number; // ms
	onActionButtonClick?: () => void | Promise<void>;
	readonly backdropDisable?: boolean;
	readonly onCloseViewNotification?: () => void;
	readonly isMiniNotification?: boolean;
	readonly isIconVisible?: boolean;
	readonly progress?: number;
	readonly onLocate?: () => void;
}

export interface INotificationProps extends INotificationParamsV5 {
	readonly onClose?: () => void;
}

/**
 * Normally you'd want to create a new notification (toast message) with the globally available "notify" method, like this:
 * notify(params);
 */

export const NotificationV5 = (props: INotificationProps) => {
	const {
		title = "",
		description = "",
		link = "",
		type = NotificationType.Message,
		className = "",
		cancelable = true,
		buttonLabel = "",
		style = {},
		onClose = Functions.emptyFunction,
		onActionButtonClick = Functions.emptyFunction,
		onCloseViewNotification = Functions.emptyFunction,
		lifeTime = 5000,
		backdropDisable = false,
		isMiniNotification = false,
		isIconVisible = true,
		progress,
		onLocate,
	} = props;
	const _ref = useRef<HTMLDivElement>(null);
	const _isMounted = useRef<boolean>(false);
	const _timeoutId = useRef<number>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const onClick = async () => {
		try {
			setIsLoading(true);
			await (onActionButtonClick() as unknown as Promise<void>);
			setIsLoading(false);
			onCloseLocal();
		} catch (error) {
			console.warn("error ", error);
		}
	};

	const onCloseLocal = useCallback(() => {
		if (_isMounted.current) {
			if (_ref.current) {
				_ref.current.style.transform = "scaleY(0)";
			}
			onCloseViewNotification();
			setTimeout(onClose, 300); // 0.3s -> from css transition
		}
		clearTimeout(_timeoutId.current);
	}, [onClose, onCloseViewNotification]);

	const IsLifeTimeLimited = MathUtils.isValidNumber(lifeTime) && lifeTime > 0;

	const getColor = () => {
		switch (type) {
			case NotificationType.Error:
				return colorPalette.negative.c600;
			case NotificationType.Message:
				return colorPalette.primary.c500Primary;
			case NotificationType.Success:
				return colorPalette.positive.c500Primary;
			case NotificationType.Warning:
				return colorPalette.warning.c500Primary;
		}
	};

	const getIcon = () => {
		if (isIconVisible) {
			switch (type) {
				case NotificationType.Error:
					return <XMarkIcon />;
				case NotificationType.Message:
					return <InformationAltIcon />;
				case NotificationType.Success:
					return <CheckIcon />;
				case NotificationType.Warning:
					return <ExclamationAltIcon />;
			}
		}
	};

	useEffect(() => {
		_isMounted.current = true;

		if (_ref.current) {
			_ref.current.style.transform = "scaleY(1)";
		}

		if (IsLifeTimeLimited) {
			_timeoutId.current = window.setTimeout(onCloseLocal, lifeTime);
		}

		return () => {
			_isMounted.current = false;
			clearTimeout(_timeoutId.current);
		};
	}, [IsLifeTimeLimited, lifeTime, onCloseLocal]);

	const renderNotificationContent = () => {
		const spinnerColor = getColor();
		const icon = getIcon();

		return (
			<NotificationV5Styled
				className={`${NotificationType[type]?.toLowerCase()} ${className || ""}`}
				style={style || {}}
				ref={_ref}
				data-cy="notification"
				$color={spinnerColor}
				$isMiniNotification={props.isMiniNotification}
			>
				<NotificationContentStyled>
					{icon && <NotificationIconStyled $color={spinnerColor}>{icon}</NotificationIconStyled>}
					{MathUtils.isValidNumber(progress) && isMiniNotification && type === NotificationType.Message && (
						<CountdownCircleTimer
							key={progress} // Forces re-render on progress update
							isPlaying
							duration={100}
							initialRemainingTime={progress} // Start from progress value (0 → 100)
							colors={spinnerColor as `#${string}`}
							size={32}
							strokeWidth={2}
							rotation="counterclockwise"
						/>
					)}
					<NotificationTextStyled>
						{typeof title === "string" || typeof title === "number" ? <NotificationTitleStyled>{title}</NotificationTitleStyled> : title}
						{description && (
							<div
								className="description"
								dangerouslySetInnerHTML={{__html: description}}
							/>
						)}
						{type === NotificationType.Announcement && (
							<a
								className="textLink"
								href={link}
							>
								Read more
							</a>
						)}
					</NotificationTextStyled>
					<ActionButtonsStyled>
						{buttonLabel && onActionButtonClick && (
							<ButtonV5
								className={`button buttonLabel ${NotificationType[type]?.toLowerCase()}`}
								disabled={isLoading}
								label={isLoading ? "Loading..." : buttonLabel}
								onClick={onClick}
							/>
						)}
						{onLocate && (type === NotificationType.Success || type === NotificationType.Error) && (
							<IconButtonV5
								IconComponent={PinIcon}
								style={{color: spinnerColor}}
								onClick={() => onLocate?.()}
							/>
						)}
						{icon && (
							<CancelButtonStyled data-cy="cancel-button">
								{IsLifeTimeLimited && (
									<CountdownCircleTimer
										isPlaying
										duration={lifeTime / 1000}
										colors={spinnerColor as `#${string}`}
										size={32}
										strokeWidth={2}
									/>
								)}
								{cancelable && (
									<IconButtonV5
										IconComponent={XMarkIcon}
										onClick={onCloseLocal}
									/>
								)}
							</CancelButtonStyled>
						)}
					</ActionButtonsStyled>
				</NotificationContentStyled>
			</NotificationV5Styled>
		);
	};

	if (backdropDisable) {
		return (
			<DomPortal destination={appHolder()}>
				<div className="notificationBackdrop vbox alignCenter">{renderNotificationContent()}</div>
			</DomPortal>
		);
	} else {
		return renderNotificationContent();
	}
};

const NotificationV5Styled = styled.div<{$color?: string; $isMiniNotification?: boolean}>`
	z-index: ${zIndex.notification};
	position: relative;
	background: ${colorPalette.white};
	width: ${(props) => (props.$isMiniNotification ? "432px" : "1000px")};
	height: 80px;
	margin-top: 24px;
	justify-content: space-between;
	overflow: hidden;
	transform-origin: bottom center;
	transition: transform 0.3s ease-out;
	border-radius: ${radius.md};
	box-shadow: 0px 4px 50px 0px #00000033;
	border: 2px solid;
	border-left: ${baseDistance.sm} solid;
	border-color: ${(props) => props.$color};
	${FlexCenterStyle}

	&.message {
		background-color: ${colorPalette.primary.c200Light};
	}

	&.warning {
		background-color: ${colorPalette.warning.c200Light};
	}

	&.success {
		background-color: ${colorPalette.positive.c200Light};
	}

	&.error {
		background-color: ${colorPalette.negative.c200Light};
	}

	&.announcement {
		width: 100%;
		max-width: initial;
		margin: 0;
		top: 0;
		background: ${colorPalette.warning.c500Primary};
		border: none;
		height: 36px;
		box-shadow: none;

		.details {
			${FlexCenterStyle}

			h3 {
				margin-right: 5px;
			}

			.textLink {
				margin-left: 15px;
			}
		}

		.actions {
			.cancel.icon {
				width: 16px;
				height: 16px;
				color: var(--text1);
			}
		}
	}

	.notificationBackdrop {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: ${zIndex.popup};
		background-color: rgba(0, 0, 0, 0.45);

		.Notification {
			margin-top: 20px;
		}
	}
`;

const NotificationIconStyled = styled.div<{$color?: string}>`
	${FLEXCENTER}
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background-color: ${(props) => props.$color};

	svg {
		height: 16px;
		color: ${colorPalette.white};
	}
`;

const NotificationContentStyled = styled.div`
	${FlexCenterStyle};
	padding: ${baseDistance.sm};
	gap: ${baseDistance.sm};
	width: 100%;
`;

const NotificationTextStyled = styled.div`
	color: ${colorPalette.gray.c950};
	${VerticalFlexStyle};
	min-width: 0;
	flex: 1;

	.description {
		@supports (-webkit-line-clamp: 2) {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: initial;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		}
	}

	div {
		font-weight: ${fontWeight.normal};
		font-size: ${fontSize.md};
		overflow: hidden;
		word-break: break-word;
		color: ${colorPalette.gray.c700Dark};
		max-width: 824px;
	}

	p {
		font-weight: ${fontWeight.thin};
		font-size: ${fontSize.sm};
		line-height: 16px;
		margin: 0;
	}

	ul {
		margin: 10px 0;

		li {
			font-family: "Roboto";
			font-size: ${fontSize.md};
			font-weight: ${fontWeight.normal};
			line-height: 20px;
		}
	}
`;

export const NotificationTitleStyled = styled.h3`
	font-family: "Roboto";
	font-style: normal;
	font-weight: ${fontWeight.bold};
	font-size: ${fontSize.lg};
	line-height: 16px;
	padding: 0;
	margin: 2px 0 3px;
	color: ${colorPalette.gray.c950};
	${ELLIPSIS};

	span {
		color: ${colorPalette.primary.c500Primary};
	}
`;

const ActionButtonsStyled = styled.div`
	display: flex;
	align-items: center;
	gap: ${baseDistance.sm};

	.button {
		padding: ${baseDistance.md};
		white-space: nowrap;
		border-radius: ${radius.sm};

		&.message {
			background-color: ${colorPalette.primary.c500Primary};
		}

		&.warning {
			background-color: ${colorPalette.warning.c500Primary};
		}

		&.success {
			background-color: ${colorPalette.positive.c500Primary};
		}

		&.error {
			background-color: ${colorPalette.negative.c600};
		}
	}
`;

const CancelButtonStyled = styled.div`
	width: 32px;
	height: 32px;
	display: flex;
	justify-content: center;

	svg {
		cursor: pointer;
		z-index: 1;
	}

	.IconButton {
		position: absolute;
	}
`;
